import { useLatestChanges } from './use-shared-value';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCollectionValuesResolution } from './shared-value-resolving';
import { useSharedValueStoreContext } from './context';
import { useSingletonInitialization } from './use-singleton-initialization';
export function useSharedCollection(key, keys) {
    if (keys === undefined) {
        return useAllSharedCollection(key);
    }
    else {
        return useFilteredSharedCollection(key, keys);
    }
}
function useFilteredSharedCollection(key, keys) {
    var context = useSharedValueStoreContext();
    var _a = useState(function () { return context.get(key, keys); }), values = _a[0], setValues = _a[1];
    useCollectionValuesResolution(key, keys, values);
    useLatestChanges(function () { return context.changes(key, keys); }, function () {
        setValues(context.get(key, keys));
    });
    useEffect(function () { return setValues(context.get(key, keys)); }, [context, keys]);
    return values;
}
function useAllSharedCollection(key) {
    var context = useSharedValueStoreContext();
    var _a = useState(function () { return context.getAll(key); }), values = _a[0], setValues = _a[1];
    useLatestChanges(function () { return context.anyChanges(key); }, function () {
        setValues(context.getAll(key));
    }, [context]);
    useEffect(function () { return setValues(context.getAll(key)); }, [context]);
    return values;
}
/**
 * This hook defines an async initialization that will only be called once globally.
 * This allows a developer to define the init logic of a shared value inside it's hook
 * without initialization being triggered multiple times
 * @deprecated Use initializers instead
 * @param key The shared value key
 * @param init An async initialization function that returns the inital values of the collection
 */
export var useSharedCollectionInitialization = function (key, init) {
    var context = useSharedValueStoreContext();
    var apply = function (values) {
        context.update(key, values);
    };
    return useSingletonInitialization(key, init, apply);
};
export function useReadCollectionValue(key) {
    var context = useSharedValueStoreContext();
    return useCallback(function (valueKey) { return context.get(key, valueKey); }, [context, key]);
}
export function useSingleCollectionValue(key, valueKey) {
    var context = useSharedValueStoreContext();
    var _a = useState(function () { return context.get(key, valueKey); }), value = _a[0], setValue = _a[1];
    useLatestChanges(function () { return context.changes(key, valueKey); }, function (newValue) {
        setValue(newValue);
    }, [context, valueKey]);
    useEffect(function () { return setValue(context.get(key, valueKey)); }, [context, valueKey]);
    var updateValue = function (newValue) { return context.set(key, valueKey, newValue); };
    return [value, updateValue];
}
export function useWaitForCollectionValue(key, criteria) {
    var context = useSharedValueStoreContext();
    return function waitForValue(valueKey) {
        return context.waitFor(key, valueKey, criteria);
    };
}
/**
 * Allows the modification of a shared collection without triggering a render on components
 * that use this hook. If you need to get values out of a collection, use the useSharedCollection
 * hook.
 * @deprecated Use SharedValueEffects to do large scale modification of collections
 * @param key The key identifying the shared collection
 */
export function useModifySharedCollection(key) {
    var context = useSharedValueStoreContext();
    return useMemo(function () { return ({
        setValue: function (valueKey, value) { return context.set(key, valueKey, value); },
        update: function (update) { return context.update(key, update); },
        delete: function (valueKeys) { return context.delete(key, valueKeys); },
        clear: function () { return context.clear(key); }
    }); }, [key, context]);
}
export function toCollectionUpdate(objects, reduce) {
    return objects.reduce(function (update, obj) {
        reduce(update, obj);
        return update;
    }, {});
}
//# sourceMappingURL=use-shared-collection.js.map